import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiClock, FiFileText, FiChevronRight, FiInbox } from 'react-icons/fi';
import '../../../styles/Student.css';
import '../assessment-module-mobile.css';
import testService from '../../../services/testService';
import VideoModal from './VideoModal';
import { useAssessmentTooltips } from '../AssessmentContext';
import Layout from '../../../components/Layout/Layout';

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  try {
    const [hours, minutes] = timeStr.split(':');
    let h = parseInt(hours);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; 
    return `${h}:${m} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const MemberAssessmentDashboard = ({ withShell = false }) => {
  useAssessmentTooltips();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Video Modal State
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedTestForVideo, setSelectedTestForVideo] = useState(null);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const res = await testService.getStudentAssessments();
      
      if (res.status === 'success') {
        const processedTests = res.assessments.map(pack => {
          const now = new Date();
          const scheduledDate = pack.scheduled_date;
          const startTimeStr = pack.start_time;
          const endTimeStr = pack.end_time;
          const startDateTime = new Date(`${scheduledDate}T${startTimeStr}`);
          const endDateTime = new Date(`${scheduledDate}T${endTimeStr}`);

          const hasSubmitted = pack.submission_status === 'COMPLETED';

          let status = 'UPCOMING';
          let badgeClass = 'status-upcoming';
          
          if (hasSubmitted) {
            status = 'COMPLETED';
            badgeClass = 'status-completed';
          } else if (now >= startDateTime && now <= endDateTime) {
            status = 'ACTIVE';
            badgeClass = 'status-live';
          } else if (now > endDateTime) {
            status = 'EXPIRED';
            badgeClass = 'status-upcoming';
          } else {
            status = 'UPCOMING';
            badgeClass = 'status-upcoming';
          }

          return {
            id: pack.assessment_id,
            packId: pack.id,
            testName: pack.assessment_name,
            batchName: pack.pack_name,
            status,
            badgeClass,
            date: scheduledDate,
            startTime: startTimeStr,
            endTime: endTimeStr,
            add_video: pack.add_video === 'Yes',
            intro_videos: pack.intro_videos ? (typeof pack.intro_videos === 'string' ? JSON.parse(pack.intro_videos) : pack.intro_videos) : []
          };
        });
        setTests(processedTests.sort((a, b) => (parseInt(b.packId) || 0) - (parseInt(a.packId) || 0)));
      }
    } catch (err) {
      console.error("Failed to fetch student assessments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTakeTest = (test) => {
    if (test.add_video && test.intro_videos && test.intro_videos.length > 0) {
      setSelectedTestForVideo(test);
      setIsVideoModalOpen(true);
    } else {
      navigate(`/execution/${test.id}/${test.packId}`);
    }
  };

  const startExam = () => {
    if (selectedTestForVideo) {
      navigate(`/execution/${selectedTestForVideo.id}/${selectedTestForVideo.packId}`);
      setIsVideoModalOpen(false);
    }
  };

  const page = (
    <div className="assess-mod-page assess-mod-student-page student-dashboard">
      <div className="assess-mod-card card-custom">
        <div className="assess-mod-student-header dashboard-header">
          <h2 className="header-title">Scheduled & Live Assessments</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Manage and track your assigned test modules</p>
        </div>
        
          {loading ? (
            <div className="empty-state-container">
              <div className="loading-spinner"></div>
              <p>Loading Assessments...</p>
            </div>
          ) : tests.length > 0 ? (
            <div className="assess-mod-student-table-wrap assess-mod-table-wrap">
            <table className="assess-mod-student-table modern-table">
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tests.map((test, index) => (
                  <tr key={index}>
                    <td>
                      <div className="test-info-wrapper">
                        <div className="test-icon-box">
                          <FiFileText />
                        </div>
                        <div>
                          <div className="test-name-main">{test.testName}</div>
                          <div className="test-batch-sub">{test.batchName}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${test.badgeClass}`}>{test.status}</span>
                    </td>
                    <td>
                      <div className="schedule-item">
                          <div className="schedule-date">
                            <FiCalendar size={14} style={{ color: '#94a3b8' }} />
                            {new Date(test.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()}
                          </div>
                          <div className="time-badge-container">
                            <div className="time-card">{formatTime(test.startTime)}</div>
                            <span className="time-sep">—</span>
                            <div className="time-card">{formatTime(test.endTime)}</div>
                          </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {test.status === 'COMPLETED' ? (
                        <div className="action-text" style={{ color: '#10b981', fontWeight: 800 }}>SUBMITTED</div>
                      ) : test.status === 'ACTIVE' ? (
                        <button 
                          className="take-test-btn" 
                          onClick={() => handleTakeTest(test)}
                        >
                          Take Test
                        </button>
                      ) : test.status === 'EXPIRED' ? (
                        <div className="action-text">CLOSED</div>
                      ) : (
                        <div className="action-text">WAITING</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div className="empty-state-container">
              <div className="empty-icon-box">
                <FiInbox size={48} />
              </div>
              <h3 className="empty-title">No Assessments Assigned</h3>
              <p className="empty-subtitle">You don't have any assessments scheduled at the moment.</p>
            </div>
          )}
      </div>

      {selectedTestForVideo && (
        <VideoModal 
          isOpen={isVideoModalOpen}
          videos={selectedTestForVideo.intro_videos}
          testName={selectedTestForVideo.testName}
          onComplete={startExam}
        />
      )}
    </div>
  );

  if (withShell) {
    return <Layout noSidebar>{page}</Layout>;
  }

  return page;
};

export default MemberAssessmentDashboard;
